import {
  memo,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type PropsWithChildren,
} from "react";

import { Text, type Props as TextProps } from "@react/components/Text";
import { tw } from "classnames";

type Props = {
  linkReadLess?: string;
  linkReadMore?: string;
  maxLines?: 1 | 2 | 3 | 4 | 5 | 6;
} & TextProps;

const TruncatedTextBase = ({
  children,
  linkReadLess,
  linkReadMore,
  maxLines = 2,
  ...props
}: PropsWithChildren<Props>) => {
  const childrenRef = useRef<HTMLElement>(null);
  const [isClamped, setIsClamped] = useState<boolean>(false);
  const [isExpanded, setIsExpanded] = useState<boolean>(false);

  useEffect(() => {
    const resizeObserver = new ResizeObserver((entries) => {
      for (const entry of entries) {
        if (entry.target === childrenRef.current) {
          setIsClamped(
            childrenRef.current.scrollHeight > childrenRef.current.clientHeight
          );
        }
      }
    });
    const observeTarget = childrenRef.current;

    if (observeTarget) resizeObserver.observe(observeTarget);

    return () => {
      if (observeTarget) resizeObserver.unobserve(observeTarget);
    };
  }, []);

  const toggleClamping = useCallback(
    (e: React.MouseEvent<HTMLAnchorElement>) => {
      e.preventDefault();
      setIsExpanded(!isExpanded);
    },
    [isExpanded]
  );

  const lineClampClass = useMemo(() => {
    if (isExpanded) return "line-clamp-none";

    let className = "line-clamp-1";
    switch (maxLines) {
      case 2:
        className = "line-clamp-2";
        break;
      case 3:
        className = "line-clamp-3";
        break;
      case 4:
        className = "line-clamp-4";
        break;
      case 5:
        className = "line-clamp-5";
        break;
      case 6:
        className = "line-clamp-6";
        break;
    }
    return className;
  }, [isExpanded, maxLines]);

  return (
    <>
      <Text {...props}>
        <span className={lineClampClass} ref={childrenRef}>
          {children}
          {isExpanded && (
            <a
              className={tw("bg-transparent", "text-current", "pl-1")}
              href="#"
              onClick={(e) => toggleClamping(e)}
              role="button"
            >
              {linkReadLess}
            </a>
          )}
        </span>
        {isClamped && (
          <a
            className={tw("bg-transparent", "text-current")}
            href="#"
            onClick={(e) => toggleClamping(e)}
            role="button"
          >
            {linkReadMore}
          </a>
        )}
      </Text>
    </>
  );
};

export const TruncatedText = memo(TruncatedTextBase);
